/**
 * When nothing worth having can be reached: find the boulder that stands between Digby and a
 * target and make it move. A boulder falls once the cell under it is dug out, and rolls off
 * something rounded once the cell beside it and the one below that are clear, so the bot goes to
 * dig one of those cells ("a loosener"). Boulders are ranked by how many more boulders lie between
 * them and the nearest target. Failing that, it digs somewhere new toward the targets, so the cave
 * keeps changing.
 */

import { E } from '../sim';
import type { CaveState, Rng } from '../sim';
import { INF as UNREACHED } from './paths';
import type { Paths } from './paths';
import { isTarget } from './targets';

const { SPACE, DIRT, STEEL, BRICK, BOULDER, DIAMOND, CLOCK, EXPANDING_WALL } = E;
const { OUTBOX_CLOSED, OUTBOX_OPEN, FIREFLY, BUTTERFLY, KEY } = E;
const INF = UNREACHED;
const targetEl = isTarget;
/** Random spread, so a stuck bot does not keep choosing the same cell. */
const JITTER = 12;

function rounded(el: number): boolean {
  return el === BOULDER || el === DIAMOND || el === BRICK || el === EXPANDING_WALL || el === CLOCK;
}

/** Ground a search from the targets may cross at no cost. */
function open(el: number): boolean {
  if (el === SPACE || el === DIRT || el === DIAMOND || el === CLOCK || el === OUTBOX_OPEN || el === OUTBOX_CLOSED) {
    return true;
  }
  return (el >= KEY && el < KEY + 3) || (el >= FIREFLY && el < BUTTERFLY + 4);
}

export class Explorer {
  /** Boulders between each cell and the nearest target (0-1 search from all targets). */
  private readonly toTarget: Int32Array;
  private readonly deque: Int32Array;

  constructor(private readonly n: number) {
    this.toTarget = new Int32Array(n);
    this.deque = new Int32Array(2 * n + 2);
  }

  private fromTargets(s: CaveState, banned: Int32Array): void {
    const { w, cells } = s;
    const n = this.n;
    const { toTarget: cost, deque } = this;
    cost.fill(INF);
    let head = n;
    let tail = n;
    for (let c = 0; c < n; c++) {
      const el = cells[c] ?? STEEL;
      // With the gate open, keys still count: a door may stand between Digby and the exit.
      const key = s.gateOpen && el >= KEY && el < KEY + 3;
      if ((!targetEl(el, s.gateOpen) && !key) || (banned[c] ?? 0) > s.scan) continue;
      cost[c] = 0;
      deque[tail++] = c;
    }
    while (head < tail) {
      const c = deque[head++] ?? 0;
      const d = cost[c] ?? 0;
      const x = c % w;
      for (let k = 0; k < 4; k++) {
        if ((k === 1 && x === w - 1) || (k === 3 && x === 0)) continue;
        const nb = k === 0 ? c - w : k === 1 ? c + 1 : k === 2 ? c + w : c - 1;
        if (nb < 0 || nb >= n) continue;
        const el = cells[nb] ?? STEEL;
        const step = open(el) ? 0 : el === BOULDER ? 1 : -1;
        if (step < 0 || d + step >= (cost[nb] ?? INF)) continue;
        cost[nb] = d + step;
        if (step === 0) deque[--head] = nb;
        else deque[tail++] = nb;
      }
    }
  }

  /** A cell to work toward, or -1 to wait where he is. */
  goal(s: CaveState, me: number, near: Paths, banned: Int32Array, rng: Rng): number {
    const { w, h, cells } = s;
    this.fromTargets(s, banned);
    const usable = (c: number): boolean =>
      c >= 0 && c !== me && cells[c] === DIRT && near.reachable(c) && (banned[c] ?? 0) <= s.scan;
    let best = -1;
    let bestScore = INF;
    const consider = (c: number, rank: number): void => {
      if (!usable(c)) return;
      const score = rank * 100 + (near.dist[c] ?? 0) + rng.int(JITTER);
      if (score < bestScore) {
        bestScore = score;
        best = c;
      }
    };
    for (let b = w; b < this.n - w; b++) {
      if (cells[b] !== BOULDER) continue;
      const rank = this.toTarget[b] ?? INF;
      if (rank >= INF) continue;
      const x = b % w;
      const below = cells[b + w] ?? STEEL;
      if (below === DIRT) consider(b + w, rank);
      else if (rounded(below)) {
        for (const side of [-1, 1]) {
          if (x + side < 0 || x + side >= w) continue;
          const beside = cells[b + side] ?? STEEL;
          const diagonal = cells[b + w + side] ?? STEEL;
          if ((beside !== SPACE && beside !== DIRT) || (diagonal !== SPACE && diagonal !== DIRT)) continue;
          // Dig the lower cell first: digging the side cell first would leave Digby beside the boulder.
          consider(diagonal === DIRT ? b + w + side : b + side, rank);
        }
      }
    }
    if (best >= 0) return best;
    for (let c = 0; c < w * h; c++) {
      if (!usable(c)) continue;
      const toward = Math.min(this.toTarget[c] ?? INF, 50) * 20;
      const score = toward + ((near.dist[c] ?? 0) >> 1) + rng.int(JITTER);
      if (score < bestScore) {
        bestScore = score;
        best = c;
      }
    }
    return best;
  }
}
